import Placeholder from '-/Custom/Placeholder';
import Table from '-/Custom/Table';
import type {
  TableColumn,
  TableRowData,
  TableSortKey,
} from '-/Custom/Table.types';
import { StyleSheet } from 'react-native';
import Box from '@/components/UI/Box';
import Text from '@/components/UI/Text';
import { type FragmentOf, graphql, readFragment } from '@/gql/graphql';

/**
 *
 * TODO - root_owner is an array due to graphql-engine limitation
 * We should deprecate this and make it an object as soon as we get the chance
 * https://github.com/hasura/graphql-engine/pull/8938
 */

export const EventTeamStandingsFragment = graphql(/* GraphQL */ `
  fragment EventTeamStandings on standing {
    event_id
    team_id
    games_played
    wins
    losses
    draws
    points
    score_for
    score_against
    score_difference
    home_win_loss_draw
    away_win_loss_draw
    home_win_draw_loss
    away_win_draw_loss
    win_loss
    away_win_loss
    home_win_loss
    win_percentage
    title
    color
    point_percentage
  }
`);
export const EventStandingsFragment = graphql(/* GraphQL */ `
  fragment EventStandings on query_root {
    event_by_pk(id: $eventId) {
      id
      root_owner {
        id
        tag_group {
          id
          standing_map
          standing_sort
        }
      }
    }

    team_standing(args: { id: $eventId }) {
      ...EventTeamStandings
    }
  }
`, [EventTeamStandingsFragment]);
const style = StyleSheet.create({
  titleCellContainer: {
    flexDirection: 'row',
    paddingRight: 8,
  },
  titleTeamColorBar: {
    width: 2,
    borderRadius: 4,
    marginLeft: 8,
  },
  titleText: {
    paddingLeft: 8,
  },
});

type StandingMap =
  | {
      alias: string;
      column: string;
      sortBy: 'asc' | 'desc';
    }
  | undefined;
type EventStandingsProps = {
  data: FragmentOf<typeof EventStandingsFragment> | undefined;
  fetching: boolean;
};
export default function EventStandings({
  data,
  fetching,
}: EventStandingsProps) {
  const standingsFragment = readFragment(EventStandingsFragment, data);
  const eventTeamStandingsFragment = readFragment(
    EventTeamStandingsFragment,
    standingsFragment?.team_standing || [],
  );
  const rootEvent = standingsFragment?.event_by_pk?.root_owner?.[0];
  const standingSort = (rootEvent?.tag_group?.standing_sort ||
    []) as TableSortKey[];
  const standingMap = (rootEvent?.tag_group?.standing_map ||
    []) as StandingMap[];
  const standingsData =
    eventTeamStandingsFragment?.map((eventTeamStanding) => ({
      ...eventTeamStanding,
      id: eventTeamStanding.team_id,
    })) || [];
  const renderTitleCell = (rowData: TableRowData) => (
    <Box style={style.titleCellContainer}>
      <Text ml="md" numberOfLines={1} maxWidth={32}>
        {rowData.title}
      </Text>
      <Box
        style={[
          style.titleTeamColorBar,
          {
            backgroundColor: rowData.color,
          },
        ]}
      />
    </Box>
  );
  const columns: TableColumn[] = [
    {
      title: '',
      key: 'title',
      sticky: true,
      initialSortDirection: 'asc',
      headerCellStyle: {
        flexGrow: 1,
        justifyContent: 'flex-end',
      },
      cellStyle: {
        flexGrow: 1,
        alignItems: 'flex-end',
      },
      renderCell: renderTitleCell,
    },
    ...standingMap.map((standing) => {
      const aliasLength = standing?.alias?.length || 0;
      return {
        title: standing?.alias || '',
        key: standing?.column || '',
        width: aliasLength > 2 ? 20 * aliasLength : 50,
        initialSortDirection: standing?.sortBy,
      };
    }),
  ];
  return !fetching && standingsData.length ? (
    <Table
      columns={columns}
      rows={standingsData}
      sortKeys={standingSort}
      highlight
    />
  ) : !fetching ? (
    <Placeholder
      icon="menu-outline"
      text="No standings data here yet. When you have two or more teams that have games scheduled, the standings table will show up here."
    />
  ) : null;
}
